import { db } from "../database/firebase";
import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
} from "firebase/firestore";

const TRANSACTIONS_COLLECTION = "inventoryTransactions";

/**
 * Log an inward transaction (Purchase)
 */
export const logInwardTransaction = async (data) => {
  try {
    const transaction = {
      type: "inward",
      date: data.date || Timestamp.now(),
      userName: data.userName,
      userId: data.userId,
      products: data.products || [],
      totalAmount: data.totalAmount || 0,
      totalValuePoints: data.totalValuePoints || 0,
      source: "purchase",
      createdAt: Timestamp.now(),
      createdBy: data.createdBy || "system",
    };

    const docRef = await addDoc(
      collection(db, TRANSACTIONS_COLLECTION),
      transaction
    );
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error("Error logging inward transaction:", error);
    throw error;
  }
};

/**
 * Log an outward transaction (Recharge)
 */
export const logOutwardTransaction = async (data) => {
  try {
    const transaction = {
      type: "outward",
      date: data.date || Timestamp.now(),
      userName: data.userName,
      userId: data.userId,
      products: data.products || [],
      totalAmount: data.totalAmount || 0,
      totalValuePoints: data.totalValuePoints || 0,
      source: "recharge",
      createdAt: Timestamp.now(),
      createdBy: data.createdBy || "system",
    };

    const docRef = await addDoc(
      collection(db, TRANSACTIONS_COLLECTION),
      transaction
    );
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error("Error logging outward transaction:", error);
    throw error;
  }
};

/**
 * Get all transactions
 */
export const getAllTransactions = async () => {
  try {
    const q = query(
      collection(db, TRANSACTIONS_COLLECTION),
      orderBy("date", "desc")
    );
    const querySnapshot = await getDocs(q);

    const transactions = [];
    querySnapshot.forEach((doc) => {
      transactions.push({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date?.toDate?.() || new Date(doc.data().date),
        createdAt:
          doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt),
      });
    });

    return transactions;
  } catch (error) {
    console.error("Error fetching transactions:", error);
    throw error;
  }
};

/**
 * Get inward transactions only
 */
export const getInwardTransactions = async () => {
  try {
    const q = query(
      collection(db, TRANSACTIONS_COLLECTION),
      where("type", "==", "inward"),
      orderBy("date", "desc")
    );
    const querySnapshot = await getDocs(q);

    const transactions = [];
    querySnapshot.forEach((doc) => {
      transactions.push({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date?.toDate?.() || new Date(doc.data().date),
        createdAt:
          doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt),
      });
    });

    return transactions;
  } catch (error) {
    console.error("Error fetching inward transactions:", error);
    throw error;
  }
};

/**
 * Get outward transactions only
 */
export const getOutwardTransactions = async () => {
  try {
    const q = query(
      collection(db, TRANSACTIONS_COLLECTION),
      where("type", "==", "outward"),
      orderBy("date", "desc")
    );
    const querySnapshot = await getDocs(q);

    const transactions = [];
    querySnapshot.forEach((doc) => {
      transactions.push({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date?.toDate?.() || new Date(doc.data().date),
        createdAt:
          doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt),
      });
    });

    return transactions;
  } catch (error) {
    console.error("Error fetching outward transactions:", error);
    throw error;
  }
};

/**
 * Get transactions by date range
 */
export const getTransactionsByDateRange = async (startDate, endDate) => {
  try {
    const q = query(
      collection(db, TRANSACTIONS_COLLECTION),
      where("date", ">=", Timestamp.fromDate(startDate)),
      where("date", "<=", Timestamp.fromDate(endDate)),
      orderBy("date", "desc")
    );
    const querySnapshot = await getDocs(q);

    const transactions = [];
    querySnapshot.forEach((doc) => {
      transactions.push({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date?.toDate?.() || new Date(doc.data().date),
        createdAt:
          doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt),
      });
    });

    return transactions;
  } catch (error) {
    console.error("Error fetching transactions by date range:", error);
    throw error;
  }
};

/**
 * Calculate transaction summary
 */
export const getTransactionSummary = (transactions) => {
  const summary = {
    totalInward: 0,
    totalOutward: 0,
    totalInwardPoints: 0,
    totalOutwardPoints: 0,
    inwardCount: 0,
    outwardCount: 0,
    balance: 0,
    balancePoints: 0,
  };

  transactions.forEach((transaction) => {
    if (transaction.type === "inward") {
      summary.totalInward += transaction.totalAmount || 0;
      summary.totalInwardPoints += transaction.totalValuePoints || 0;
      summary.inwardCount++;
    } else if (transaction.type === "outward") {
      summary.totalOutward += transaction.totalAmount || 0;
      summary.totalOutwardPoints += transaction.totalValuePoints || 0;
      summary.outwardCount++;
    }
  });

  summary.balance = summary.totalInward - summary.totalOutward;
  summary.balancePoints =
    summary.totalInwardPoints - summary.totalOutwardPoints;

  return summary;
};
